'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Image from 'next/image';
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hostEnquiryAPI } from "@/lib/api";
import { toast } from "sonner";

export const bedOptions = ["Studio", "1", "2", "3", "4", "5", "6", "7", "7+"];
export const bathOptions = ["1", "2", "3", "4", "5", "6", "7", "7+"];

interface LandlordHeroProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  formData: {
    name: string;
    mobile: string;
    email: string;
    propertyName: string;
    furnishStatus: string;
    selectedBeds: string[];
    selectedBaths: string[];
    propertyType: string;
    area: string;
  };
  setFormData: (data: any) => void;
  areaList: any[];
  handleSearch: () => void;
}

const LandlordHero: React.FC<LandlordHeroProps> = ({
  currentStep,
  setCurrentStep,
  isFilterOpen,
  setIsFilterOpen,
  formData,
  setFormData,
  areaList,
  handleSearch,
}) => {
  const [loading, setLoading] = useState(false);
  const searchFilterRef = useRef<HTMLDivElement>(null);

  const handleRestartFlow = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.mobile.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        propertyName: formData.propertyName,

        area: formData.area,
        furnishStatus: formData.furnishStatus,
        selectedBeds: formData.selectedBeds,
        selectedBaths: formData.selectedBaths,
        propertyType: formData.propertyType,
      };

      console.log(payload);

      const res = await hostEnquiryAPI.createWeb(payload);

      if (res.success) {
        setCurrentStep(3);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const getDisplayText = () => {
    if (formData.selectedBeds.length === 0 && formData.selectedBaths.length === 0) {
      return "Select Beds & Baths";
    }

    const bedsText = formData.selectedBeds.length === 0 ? "Any Beds" :
      formData.selectedBeds.length === 1 ? `${formData.selectedBeds[0]} Bed` : `${formData.selectedBeds.length} Beds`;

    const bathsText = formData.selectedBaths.length === 0 ? "Any Baths" :
      formData.selectedBaths.length === 1 ? `${formData.selectedBaths[0]} Bath` : `${formData.selectedBaths.length} Baths`;

    return `${bedsText}; ${bathsText}`;
  };

  const handleBedToggle = (option: string) => {
    handlePropertyDataChange('selectedBeds',
      formData.selectedBeds.includes(option)
        ? formData.selectedBeds.filter(bed => bed !== option)
        : [...formData.selectedBeds, option]
    );
  };

  const handleBathToggle = (option: string) => {
    handlePropertyDataChange('selectedBaths',
      formData.selectedBaths.includes(option)
        ? formData.selectedBaths.filter(bath => bath !== option)
        : [...formData.selectedBaths, option]
    );
  };

  const handleReset = () => {
    handlePropertyDataChange('selectedBeds', []);
    handlePropertyDataChange('selectedBaths', []);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handlePropertyDataChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="home" className="relative md:h-[80vh] flex items-center justify-center">
      <div className="absolute inset-0">
        <Image
          src="https://images.pexels.com/photos/3320508/pexels-photo-3320508.jpeg"
          alt="Luxury Villa"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative text-center text-white max-w-5xl w-full mx-auto px-4">
        {/* Search Filter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="w-full max-w-6xl mx-auto px-4 rounded-xl" ref={searchFilterRef}>
            {/* Dark gray container */}
            <div className="bg-black/60 shadow-lg border border-gray-700 p-8 rounded-xl">
              {/* White input field container */}
              {currentStep !== 3 && (
                <h2 className="text-2xl font-bold text-white mb-4">
                  HOW MUCH CAN YOUR PROPERTY CAN EARN YOU!
                </h2>
              )}
              <div className="bg-white rounded-lg p-2 flex items-center justify-between gap-1">
                {/* Step 1 */}
                {currentStep === 1 && (
                  <div className='flex items-center justify-between w-full gap-2'>
                    {/* Where */}
                    <div className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">

                      <Select
                        value={formData.area}
                        onValueChange={(value) => handlePropertyDataChange('area', value)}
                      >
                        <SelectTrigger
                          className="!text-gray-800 !placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none w-36"
                        >
                          <SelectValue
                            placeholder="Select Area"
                            className="!placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none"
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {areaList?.map((area: { _id: string; name: string }, idx: number) => (
                            <SelectItem key={idx} value={area._id}>{area.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Baths & Beds */}
                    <div className="relative flex-1">
                      <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="!h-12 w-full border-0 bg-white rounded-none"
                          >
                            <span className={`mr-2 ${formData.selectedBeds.length === 0 && formData.selectedBaths.length === 0 ? "text-muted-foreground font-normal" : ""}`}>{getDisplayText()}</span>
                            {isFilterOpen ? (
                              <ChevronUp className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 border-gray-200 shadow-2xl" align="start" side="bottom">
                          <div className="p-3">
                            {/* Bedrooms Section */}
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Bedrooms</h4>
                              <div className="flex flex-wrap gap-2">
                                {bedOptions.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => handleBedToggle(option)}
                                    className={`px-3 py-2 text-sm rounded-lg border transition-all duration-200 ${formData.selectedBeds.includes(option)
                                      ? "border-primary bg-primary/10 text-primary font-medium"
                                      : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                      }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Bathrooms Section */}
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Bathrooms</h4>
                              <div className="flex flex-wrap gap-2">
                                {bathOptions.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => handleBathToggle(option)}
                                    className={`px-3 py-2 text-sm rounded-lg border transition-all duration-200 ${formData.selectedBaths.includes(option)
                                      ? "border-primary bg-primary/10 text-primary font-medium"
                                      : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                      }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Reset Button */}
                            <div className="flex justify-end">
                              <Button onClick={handleReset}>
                                Reset
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Furnish Status */}
                    <div className="relative flex-1">
                      <Select
                        value={formData.furnishStatus}
                        onValueChange={(value) => handlePropertyDataChange('furnishStatus', value)}
                      >
                        <SelectTrigger
                          className="!text-gray-800 !placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none"
                        >
                          <SelectValue
                            placeholder="Furnish Status"
                            className="!placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none"
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Furnished">Furnished</SelectItem>
                          <SelectItem value="Semi-Furnished">Semi-Furnished</SelectItem>
                          <SelectItem value="Unfurnished">Unfurnished</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1"></div>

                    {/* Property Type */}
                    <div className="relative flex-1">
                      <Select
                        value={formData.propertyType}
                        onValueChange={(value) => handlePropertyDataChange('propertyType', value)}
                      >
                        <SelectTrigger
                          className="!text-gray-800 !placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none"
                        >
                          <SelectValue
                            placeholder="Property Type"
                            className="!placeholder:!text-gray-500 !placeholder:!text-sm !border-none !focus:ring-0 !outline-none"
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Apartment">Apartment</SelectItem>
                          <SelectItem value="Villa">Villa</SelectItem>
                          <SelectItem value="Townhouse">Townhouse</SelectItem>
                          <SelectItem value="Studio">Studio</SelectItem>
                          <SelectItem value="Penthouse">Penthouse</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Separator */}
                    <div className="w-px h-10 bg-gray-800 mx-1" />
                  </div>
                )}

                {/* Step 2 inline form */}
                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="p-6 text-left text-gray-900"
                  >
                    {/* <h3 className="text-2xl font-semibold mb-2">Tell us about you</h3>
                    <p className="text-gray-600 mb-6">
                      Share your contact details so we can send the custom earning report for your property.
                    </p> */}
                    <div className="grid grid-cols-4 gap-4">
                      <Input
                        placeholder="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                      />
                      <Input
                        placeholder="Mobile"
                        name="mobile"
                        value={formData.mobile}
                        onChange={(e) => handleFormChange('mobile', e.target.value)}
                      />
                      <Input
                        placeholder="Email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                      />
                      <Input
                        placeholder="Property Name"
                        name="propertyName"
                        value={formData.propertyName}
                        onChange={(e) => handleFormChange('propertyName', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-end mt-6">
                      <Button
                        variant="outline"
                        onClick={handleRestartFlow}
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {currentStep === 3 && (
                  // Thank You Message Section
                  <section className="relative flex items-center justify-center w-full">
                    <div className="container mx-auto px-4">
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="p-8"
                      >
                        {/* Success Icon */}
                        {/* <div className="flex justify-center mb-6">
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-12 h-12 text-green-600" />
                          </div>
                        </div> */}

                        {/* Thank You Message */}
                        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-2">
                          Thank You!
                        </h2>
                        <p className="text-lg text-center text-gray-600 mb-8">
                          We have received your information and will get back to you shortly.
                        </p>

                        {/* Action Button */}
                        <div className="mt-8 text-center">
                          <Button
                            onClick={() => {
                              setCurrentStep(1);
                              setFormData({
                                area: '',
                                furnishStatus: '',
                                selectedBeds: [],
                                selectedBaths: [],
                                propertyType: '',
                                name: '',
                                mobile: '',
                                email: '',
                                propertyName: ''
                              });
                            }}
                          >
                            Submit Another Property
                          </Button>
                        </div>
                      </motion.div>
                    </div>
                  </section>
                )}

                {/* Calculate Button */}
                {currentStep === 1 && (
                  <Button
                    className="text-white rounded h-full"
                    onClick={handleSearch}
                  >
                    Calculate
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LandlordHero;
